import { useCallback, useEffect, useState } from 'react';

import {
  Button,
  Chips,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Stat,
  StatusDot,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
  type TagTone,
  Pagination,
} from '@dayon/design-system';
import type { StaffRestrictionKind, RestrictionReasonCode } from '@dayon/contracts';
import { formatCount } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';

/**
 * Risk management — alert triage.
 *
 * The screen is built around one sentence: "alerts are rule output, not
 * verdicts." An alert therefore cannot be dismissed; it is CLOSED, with one of
 * three verdicts and a written note, and `risk_alert_closed_has_note` enforces
 * that at the database. Everything else here — the two panes, the case file,
 * the disabled CTAs — follows from that.
 *
 * The only peso figures on this screen come out of `risk_alert.detail`, where
 * the rule engine writes them ALREADY FORMATTED through @dayon/money — the
 * payout-ratio rule records the lifetime deposit and withdrawal totals it
 * compared. Nothing here formats an amount itself, and no endpoint returns a
 * raw centavo figure for this screen to add up: every peso number the prototype
 * showed beyond those is still called out below as the gap it is.
 */

/* ── Row shapes ────────────────────────────────────────────────────────────
   snake_case, because these come straight off the SELECT lists in
   services/api/src/modules/backoffice/operations.ts — there is no camelCase
   contract for the risk module yet. */

interface RiskAlertRow {
  id: string;
  ref: string;
  /** LOW · MEDIUM · HIGH · CRITICAL */
  severity: string;
  /** OPEN · CLEARED · RESTRICTED · ESCALATED */
  status: string;
  /** Bare jsonb. In the seeded data it carries only `{ summary }`. */
  detail: unknown;
  created_at: string;
  closing_note: string | null;
  closed_at: string | null;
  rule_code: string;
  rule: string;
  rule_version: string;
  player: string | null;
  player_ref: string | null;
  player_id: string | null;
  risk_score: string | null;
  brand: string | null;
  closed_by: string | null;
  /** Withdrawals for this player in AWAITING_CHECKER or HELD_BY_RISK. */
  pending_withdrawals: string;
  /**
   * How many withdrawals THIS alert is holding.
   *
   * Different from the count above, and the difference is the whole reason the
   * Critical sentence in the timeline could be false: `pending_withdrawals`
   * counts everything queued for the player, whoever queued it and whyever it
   * is held. This counts the rows stamped `RISK_ALERT <ref>` by
   * `risk.reconcile_critical_holds` — the ones this alert actually put there.
   */
  held_by_this_alert: string;
}

interface RiskRuleRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  severity: string;
  version: string;
  enabled: boolean;
  config: Record<string, unknown> | null;
  fired_30d: string;
  /**
   * Whether ANYTHING evaluates this rule — from `RISK_RULE_EVALUATION` in
   * @dayon/db, which the worker that raises the alerts reads the same copy of.
   *
   * `enabled` is not this. All four shipped rules are enabled, and two of them
   * used to key on tables that did not exist — so the rule book printed four
   * live controls over an engine that could measure two, and the silence from
   * the other two read as an absence of the behaviour rather than of the
   * detector. `risk.account_link` closed that; all four are evaluated now, and
   * this stays false for a rule code the platform has no evaluator for.
   */
  evaluated: boolean;
  /** What it measures AND WHAT IT CANNOT SEE. Never "coming soon". */
  evaluation_detail: string;
  /**
   * WHETHER THE RULE HAS ANYTHING TO READ — the second door on the same trap.
   *
   * A rule can be enabled, evaluated, correct, and reading an empty table. The
   * two link rules depend on `risk.account_link` rows the API writes only when
   * `RISK_LINK_KEY` is set, so "evaluated every five minutes" over nothing is
   * exactly the sentence this screen used to get wrong, one level down. These
   * three fields are COUNTED FROM THE ROWS by the API and printed as they come.
   *
   * Null on the two rules that read `payment.*` directly: they have no coverage
   * question, and "0 of 0" beside them would be a figure that means nothing on
   * a screen where figures are read as meaning something.
   */
  coverage_subject: string | null;
  coverage_linked: string | null;
  coverage_total: string | null;
  /**
   * WHETHER THE STORED THRESHOLD IS ONE THE ENGINE CAN READ.
   *
   * `risk.evaluate-rules` skips a rule whose config it cannot parse — that
   * isolation is deliberate, so one bad threshold cannot disarm the other
   * three — and without this field the screen would print such a rule as
   * "Evaluated · Every 5 min" while the engine refused it every five minutes
   * and said so only in a log nobody reads.
   *
   * NULL means the platform has no shape for this rule code at all, which is a
   * different statement from "this threshold is wrong" and reads differently
   * beside a rule nothing evaluates.
   */
  threshold_readable: boolean | null;
  /** Which field, and why, in the words the contract refused it with. */
  threshold_problem: string | null;
}

interface RiskCaseRow {
  id: string;
  ref: string;
  /** OPEN · CONFIRMED · CLEARED · INCONCLUSIVE */
  status: string;
  summary: string | null;
  created_at: string;
  closed_at: string | null;
  player: string;
  player_ref: string;
  player_id: string;
  opened_by: string | null;
  closed_by: string | null;
  alert_count: string;
  /** Written findings on the file. There are no attachments; see below. */
  finding_count: string;
}

/** One written finding, or the sentence a case was closed on. */
interface RiskCaseNote {
  id: string;
  kind: 'FINDING' | 'RESOLUTION';
  body: string;
  author_label: string;
  created_at: string;
}

interface RiskCaseFile {
  /* `finding_count` is deliberately omitted: the detail endpoint returns the
     findings THEMSELVES, so a count beside them would be a second source for
     the same number and the two would eventually disagree. */
  case: Omit<RiskCaseRow, 'finding_count'> & { brand: string | null; risk_score: string | null };
  findings: RiskCaseNote[];
  /** The API's own answer to "can I attach the screenshot?" — see the panel. */
  attachments: { supported: boolean; reason: string };
}

/** How a case ended. OPEN is a state, not a verdict, so it is not offered. */
type CaseVerdict = 'CONFIRMED' | 'CLEARED' | 'INCONCLUSIVE';

const CASE_STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  CONFIRMED: 'Confirmed',
  CLEARED: 'Cleared',
  INCONCLUSIVE: 'Inconclusive',
};

const CASE_VERDICTS: readonly { key: CaseVerdict; label: string; hint: string }[] = [
  {
    key: 'CONFIRMED',
    label: 'Confirmed',
    hint: 'The behaviour was what the alert said it was.',
  },
  { key: 'CLEARED', label: 'Cleared', hint: 'Checked, and there is nothing in it.' },
  {
    key: 'INCONCLUSIVE',
    label: 'Inconclusive',
    hint: 'The evidence did not settle it. Recorded as unsettled rather than as cleared.',
  },
];

type Verdict = 'CLEARED' | 'RESTRICTED' | 'ESCALATED';

const SEVERITY_LABEL: Record<string, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

/**
 * How many alerts a page holds.
 *
 * 50, not the platform's usual 25. This is a queue somebody works down rather
 * than a roster they look things up in, and the endpoint returned 100 in one
 * go until now — halving what an operator sees per screen while fixing the fact
 * that they could not see past it would have traded one complaint for another.
 */
const PER_PAGE = 50;

/** What the server counts over the whole filtered set — never derived here. */
interface AlertSummary {
  open: number;
  criticalOpen: number;
  byStatus: Record<string, number>;
  heldWithdrawals: number;
  heldPlayers: number;
}

/** The verdict counts the cases route returns beside its (capped) list. */
interface CaseSummary {
  confirmed30d: number;
  cleared30d: number;
  inconclusive30d: number;
  openNow: number;
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  CLEARED: 'Cleared',
  RESTRICTED: 'Restricted',
  ESCALATED: 'Escalated',
};

/**
 * WHY THE LIST IS EMPTY, said in the terms the operator just used.
 *
 * It read "No open alerts for this filter" whatever was on screen. Pick the
 * Cleared chip with nothing cleared and it said there were no OPEN alerts —
 * while 458 were open. That is the opposite of the truth, and it is the one
 * sentence a risk officer must never be told wrongly. Search for something that
 * matches nothing and it said the same again, never mentioning the search, so a
 * typo in the box read as a quiet platform.
 *
 * Built from the two controls that can empty this list, because those are the
 * two the operator can undo. Brand comes last: it is set once and left, while
 * the other two are the ones being moved.
 *
 * A function rather than an expression inside the component so that what it
 * actually says can be asserted, sentence by sentence, instead of inferred from
 * the source.
 */
export function emptyStateFor(input: {
  status: string;
  query: string;
  /** The brand name when one is selected, null across every brand. */
  brandLabel: string | null;
}): { title: string; hint: string } {
  const query = input.query.trim();
  const narrowed = input.status !== 'ALL';

  const parts = [narrowed ? `${(STATUS_LABEL[input.status] ?? input.status).toLowerCase()} alerts` : 'alerts'];
  if (query !== '') parts.push(`matching \u201c${query}\u201d`);
  if (input.brandLabel !== null) parts.push(`on ${input.brandLabel}`);

  return {
    title: `No ${parts.join(' ')}`,
    /* The undo that matches what they did. "Rules keep running" is reassurance
       about the platform; it is the wrong answer to a mistyped reference, where
       the useful thing to say is that the box is what emptied the list. */
    hint:
      query !== ''
        ? 'Check the reference, or clear the search to see the queue again.'
        : narrowed
          ? 'Rules keep running in the background. Pick All to see every alert.'
          : 'Rules keep running in the background.',
  };
}

/**
 * The design's chip row also offered "Investigating". `risk_alert.status` has
 * no such value — it is OPEN · CLEARED · RESTRICTED · ESCALATED — so the chip
 * is not offered rather than offered and always empty. The notice under the
 * list says so.
 */
const STATUS_CHIPS: readonly { key: 'ALL' | keyof typeof STATUS_LABEL; label: string }[] = [
  { key: 'ALL', label: 'All' },
  ...Object.keys(STATUS_LABEL).map((s) => ({ key: s, label: STATUS_LABEL[s]! })),
];

/** Severity tone. Critical is the only one that outlines in the card list;
    in the case header everything below Critical flattens to neutral. */
function severityTone(severity: string, header = false): TagTone {
  if (severity === 'CRITICAL') return 'outline';
  if (header) return 'neutral';
  return severity === 'HIGH' ? 'neutral' : 'quiet';
}

/** Status tone. Cleared is the only settled-and-fine outcome, so it is the
    only filled chip; in the case header the rest outline rather than sit flat. */
function statusTone(status: string, header = false): TagTone {
  if (status === 'CLEARED') return 'accent';
  if (header) return 'outline';
  return status === 'RESTRICTED' ? 'outline' : 'neutral';
}

function relative(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'Yesterday';
  return `${Math.round(hours / 24)}d ago`;
}

/** 24-hour, because a timeline read at 09:12 must not be ambiguous. */
const clock = new Intl.DateTimeFormat('en-PH', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function detailSummary(detail: unknown): string | null {
  if (detail && typeof detail === 'object' && 'summary' in detail) {
    const value = (detail as { summary: unknown }).summary;
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

/** Everything in `detail` that is not the headline sentence is evidence. */
function detailSignals(detail: unknown): { label: string; value: string }[] {
  if (!detail || typeof detail !== 'object') return [];
  return Object.entries(detail as Record<string, unknown>)
    .filter(([key]) => key !== 'summary')
    .map(([key, value]) => ({
      label: key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
      value: typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value),
    }));
}

/** The threshold lives in `risk_rule.config`. All four shipped rules carry a
    real one now — a rule the engine evaluates is SKIPPED if its threshold
    cannot be read, so an empty config would be a control refused every five
    minutes. The null branch is still live and still reached: a rule code
    inserted by hand that this console has no shape for holds `{}`, and the
    column says "not stored" rather than printing a blank cell. */
function thresholdOf(config: Record<string, unknown> | null): string | null {
  const entries = Object.entries(config ?? {});
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => `${k.replace(/_/g, ' ')} ${String(v)}`).join(' · ');
}

/* ── Table columns ─────────────────────────────────────────────────────── */

/**
 * ENABLED IS NOT ARMED, AND ARMED IS NOT LOOKING AT ANYTHING.
 *
 * The single most misleading thing this screen used to show: four rules, all
 * "Enabled", over an engine that could measure two of them. SHARED_INSTRUMENT
 * needed a payment-instrument link table and BONUS_ABUSE needed something to
 * key two accounts together; neither existed, so neither rule could raise
 * anything — and their silence read as "no such behaviour on this platform"
 * rather than as "nothing is looking". `risk.account_link` is that table, and
 * all four rules are evaluated now.
 *
 * Which closes the first gap and opens the second, so this column answers BOTH
 * questions rather than only the one it used to:
 *
 *   · `evaluated` — does anything measure this rule at all. From the API, which
 *     reads the same `RISK_RULE_EVALUATION` the worker does. Still false for a
 *     rule code the platform has no evaluator for, and `evaluation_detail` then
 *     names what is missing rather than saying "not implemented".
 *   · COVERAGE — does the thing it measures actually have rows. Counted by the
 *     API from `risk.account_link` against the requests and registrations it
 *     should cover, and printed as it comes. A rule reading an empty table
 *     shows "0 of 12", which is a sentence an operator can act on; without it,
 *     that rule and a rule finding nothing look identical.
 */
const evaluatedColumn: Column<RiskRuleRow> = {
  key: 'evaluated',
  header: 'Evaluated',
  render: (r) => {
    const covered =
      r.coverage_subject === null || r.coverage_linked === null || r.coverage_total === null
        ? null
        : { linked: Number(r.coverage_linked), total: Number(r.coverage_total) };
    /* Armed, and refused every tick by its own threshold. Ranked ABOVE the
       coverage line because it is the stronger statement: a rule the engine
       skips is not measuring anything at all, whatever its coverage says. */
    const skipped = r.evaluated && r.threshold_readable === false;
    /* Blind, not broken: the rule runs and there is nothing for it to read.
       `total === 0` is not blindness — there were no requests to link — so it
       stays green. */
    const blind = Boolean(covered && covered.total > 0 && covered.linked === 0);
    return (
      <span title={r.threshold_problem ?? r.evaluation_detail}>
        <StatusDot
          tone={!r.evaluated || skipped || blind ? 'attention' : 'ok'}
          label={
            !r.evaluated
              ? 'Nothing evaluates this rule'
              : skipped
                ? 'Skipped every tick: its threshold cannot be read'
                : 'Evaluated every 5 minutes'
          }
        />{' '}
        {r.evaluated ? 'Every 5 min' : 'Not evaluated'}
        {skipped ? (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            Skipped: the engine cannot read this threshold, so this rule raises
            nothing. {r.threshold_problem}
          </div>
        ) : null}
        {covered ? (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            <span className="tabular">
              {formatCount(covered.linked)} of {formatCount(covered.total)}
            </span>{' '}
            {r.coverage_subject}
          </div>
        ) : null}
      </span>
    );
  },
};

const ruleColumns: Column<RiskRuleRow>[] = [
  {
    key: 'rule',
    header: 'Rule',
    // The prototype printed an "Automatic action" sub-line per rule. No column
    // stores it, so the sub-line is the rule's own description — what the rule
    // looks for, which is at least true.
    render: (r) => (
      <>
        {r.name}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {r.description ?? `${r.code} · v${r.version}`}
        </div>
      </>
    ),
  },
  {
    key: 'severity',
    header: 'Severity',
    secondary: true,
    render: (r) => (
      <Tag tone={severityTone(r.severity)}>{SEVERITY_LABEL[r.severity] ?? r.severity}</Tag>
    ),
  },
  {
    key: 'fired',
    header: 'Fired 30d',
    numeric: true,
    render: (r) => <span className="tabular">{formatCount(Number(r.fired_30d))}</span>,
  },
  evaluatedColumn,
];

const thresholdColumns: Column<RiskRuleRow>[] = [
  {
    key: 'rule',
    header: 'Rule',
    render: (r) => (
      <>
        {r.name}
        <div className="text-subtle tabular" style={{ fontSize: '11px' }}>
          {r.code}
        </div>
      </>
    ),
  },
  {
    key: 'threshold',
    header: 'Threshold',
    render: (r) => {
      const threshold = thresholdOf(r.config);
      return threshold ? (
        <span>{threshold}</span>
      ) : (
        <span className="text-subtle">not stored</span>
      );
    },
  },
  {
    key: 'severity',
    header: 'Severity',
    render: (r) => (
      <Tag tone={severityTone(r.severity)}>{SEVERITY_LABEL[r.severity] ?? r.severity}</Tag>
    ),
  },
  {
    // (code, version) is the rule's identity — this column IS the "thresholds
    // are versioned, a change takes effect on new events only" rule, made visible.
    key: 'version',
    header: 'Version',
    numeric: true,
    secondary: true,
    render: (r) => <span className="tabular">v{r.version}</span>,
  },
  {
    key: 'enabled',
    header: 'State',
    secondary: true,
    render: (r) => (
      <span className="row">
        <StatusDot tone={r.enabled ? 'ok' : 'quiet'} label={r.enabled ? 'Enabled' : 'Disabled'} />
        <span>{r.enabled ? 'Enabled' : 'Disabled'}</span>
      </span>
    ),
  },
  {
    key: 'fired',
    header: 'Fired 30d',
    numeric: true,
    render: (r) => <span className="tabular">{formatCount(Number(r.fired_30d))}</span>,
  },
  evaluatedColumn,
];

/* ── Close-verdict copy ────────────────────────────────────────────────────
   One dialog, three verdicts. The Restrict body is the design's copy verbatim;
   the `gap` line beside it is what the endpoint ACTUALLY does, because the
   design copy promises side effects that POST /close does not perform.

   THERE IS A THIRD LINE NOW, AND IT IS THE ONE THE DIALOG WAS MISSING. `body`
   says what the press does and `gap` says what it does not; between them they
   never said that the press happens ONCE. The close route refuses a verdict on
   an alert that is no longer open — a second one would overwrite the first
   verdict and the name of whoever gave it — and this service has no reopen at
   all, so a verdict given by mistake cannot be taken back from any screen in
   the console. `beyond` carries that, and then the way back where there is one:

   · Restricted — the control comes off from Account controls on the player's
     own profile, which is a real panel with a real Lift button and the only
     route back. Worth naming here, because a risk officer who assumes this
     dialog is where the control is managed will look in the wrong place.
   · Escalated — closing recomputes the platform's own withdrawal holds for the
     player whatever the verdict was, not only for Cleared, so held withdrawals
     go back in front of a checker as the case opens. Cleared's `gap` already
     explains that release in full, which is why Cleared's `beyond` does not say
     it a second time, and Restricted's does not either: a Restricted verdict
     that blocks withdrawals has the release and the block landing together, and
     "back in front of a checker" on its own would read as "about to be paid"
     when the block refuses the payout outright. */

/* True of all three verdicts, written once. Three copies of one sentence become
   three slightly different sentences within a year, and the one that matters
   most here is the one an operator would otherwise test by accident. */
const VERDICT_IS_FINAL =
  'A verdict is recorded once: this alert takes no second one, and nothing in the console reopens it.';

const CLOSE_COPY: Record<Verdict, { cta: string; body: string; beyond: string; gap: string }> = {
  CLEARED: {
    cta: 'Clear alert',
    body: 'The alert is closed as cleared. Your note, your name and the time are written to it and cannot be edited afterwards.',
    beyond: VERDICT_IS_FINAL,
    gap: 'If this alert put withdrawals on hold, closing it takes that hold off in the same transaction — they go back to Awaiting checker and the response says which. A hold somebody placed by hand, or one another open Critical alert is still holding, is left exactly as it is.',
  },
  RESTRICTED: {
    cta: 'Restrict player',
    body: 'The control you choose is applied to the player immediately, in the same transaction as the verdict.',
    beyond: `${VERDICT_IS_FINAL} Taking the control off again is a separate decision on the player’s own profile, under Account controls.`,
    gap: 'The player is NOT notified. The platform can send mail, and there is no template for a restriction notice and no push provider — a message drafted from a risk verdict is a disclosure decision nobody has made — so tell them by whatever channel your process uses. “The case stays open” refers to the separate risk case, not to this alert: this alert does close.',
  },
  ESCALATED: {
    cta: 'Escalate to compliance',
    body: 'The alert is closed as escalated and a risk case is opened on this player in the same transaction — or your note is added to the case they already have. The case then takes written findings and a verdict.',
    beyond: `${VERDICT_IS_FINAL} Closing it also takes this alert’s hold off any withdrawals it was holding: they go back to Awaiting checker and can be approved while Compliance still has the case open.`,
    /* No SAR or compliance-referral table exists in the schema; the escalation itself is
       recorded on the alert and opens (or adds to) a risk case. */
    gap: 'The platform still cannot draft a suspicious-activity report (SAR) or alert Compliance by itself, so no SAR draft is created and nobody is notified: escalating records the escalation and creates the CASE FILE with its audit trail, and telling Compliance is still yours to do. An alert raised against no player opens no case, and the response says so.',
  },
};

/**
 * Success copy.
 *
 * Deliberately NOT the prototype's wording. Its toasts announced a released
 * withdrawal hold, a notified player and a drafted SAR; none of those happen.
 * A toast that reports work the platform did not do is the same lie as a
 * fabricated figure, and it is one the operator will stop checking.
 */
const CLOSE_TOAST: Record<Verdict, (ref: string, player: string) => string> = {
  CLEARED: (ref) => `${ref} cleared · closing note saved to the alert`,
  RESTRICTED: (ref, player) => `${ref} closed as restricted · verdict recorded against ${player}`,
  ESCALATED: (ref) => `${ref} escalated · verdict and note recorded`,
};

/* ── Rule configuration ────────────────────────────────────────────────────
   The threshold SHAPE is decided by the rule code, exactly as the contract
   decides it server-side: VELOCITY_DEPOSIT reads deposits and withinSeconds,
   PAYOUT_RATIO reads ratioBp. Both are integers with real bounds, so the form
   refuses the same values the API refuses rather than sending them to be told.

   ALL FOUR SHIPPED RULES ARE HERE NOW. BONUS_ABUSE and SHARED_INSTRUMENT held
   an empty config for as long as nothing evaluated them; both are evaluated
   now, and the engine SKIPS a rule whose threshold it cannot read, so a rule
   with no editable threshold would be a control refused every five minutes.

   The map is still the closed set it was, and that is what it is for: a rule
   code the console does not know has no entry, its editor stays shut, and the
   notice under this dialog says why — the branch these two used to occupy. */

interface ThresholdField {
  key: string;
  label: string;
  hint: string;
  min: number;
  max: number;
}

const THRESHOLD_SHAPE: Record<string, readonly ThresholdField[]> = {
  VELOCITY_DEPOSIT: [
    {
      key: 'deposits',
      label: 'Deposits',
      hint: 'How many deposits inside the window. At least 2 — one deposit is not a velocity.',
      min: 2,
      max: 1000,
    },
    {
      key: 'withinSeconds',
      label: 'Within, in seconds',
      hint: '30 seconds to 24 hours. A window of zero would fire on every deposit.',
      min: 30,
      max: 86_400,
    },
  ],
  PAYOUT_RATIO: [
    {
      key: 'ratioBp',
      label: 'Payout ratio, in basis points',
      hint: '9000 bp is 90%. Basis points, not a decimal, so the threshold does not depend on binary rounding.',
      min: 100,
      max: 50_000,
    },
  ],
  SHARED_INSTRUMENT: [
    {
      key: 'accounts',
      label: 'Accounts on one payout instrument',
      hint: 'At least 2 — one account is not a shared instrument. Shipped at 3: two accounts paying out to one wallet is a couple with one bank account, and this rule is Critical, so a false positive is somebody’s money held.',
      min: 2,
      max: 50,
    },
    {
      key: 'withinDays',
      label: 'Within, in days',
      hint: 'How far back a payout request may reach to join a cluster. Up to two years.',
      min: 1,
      max: 730,
    },
  ],
  BONUS_ABUSE: [
    {
      key: 'accounts',
      label: 'Accounts from one signup origin',
      hint: 'At least 2. Shipped at 3, because a shared address is ordinary — a household, an office, or the thousands of subscribers behind one mobile carrier address.',
      min: 2,
      max: 50,
    },
    {
      key: 'withinDays',
      label: 'Within, in days',
      hint: 'Up to 180. Shorter than the instrument rule on purpose: a signup origin includes the browser string, and a browser update changes it, so a long window matches less rather than reaching further.',
      min: 1,
      max: 180,
    },
  ],
};

/** Why a rule was retuned or switched off. Closed, because a review counts them. */
const RULE_REASONS: readonly { key: string; label: string }[] = [
  { key: 'THRESHOLD_TUNING', label: 'Threshold tuning' },
  { key: 'FALSE_POSITIVE_RATE', label: 'False positive rate' },
  { key: 'MISSED_DETECTION', label: 'Missed detection' },
  { key: 'REGULATORY_GUIDANCE', label: 'Regulatory guidance' },
  { key: 'INCIDENT_RESPONSE', label: 'Incident response' },
  { key: 'RULE_RETIRED', label: 'Rule retired' },
];

/**
 * WHAT THE EXPORT CAN ACTUALLY ASK FOR.
 *
 * The form used to offer three scopes, three periods, three formats and three
 * delivery channels against an engine that does none of it. "Confirmed fraud
 * only" was the worst of them: nothing anywhere records a fraud confirmation,
 * so that option could never have returned a row, and it sat first-class beside
 * two that could.
 *
 * What is left is what the alert table holds. The file is CSV and you download
 * it — there is no other format and no delivery channel, and saying so once is
 * better than a menu of three that all do the same thing.
 */
const EXPORT_SCOPES = ['Open alerts only', 'All alerts in the window'] as const;

export function Risk() {
  const { brandId, brands, session, allowed } = useSession();
  /**
   * The capability this whole module needs, read once.
   *
   * Hoisted out of the refusal at the bottom because THE REFUSAL IS NOT THE ONLY
   * PLACE THAT NEEDS IT: an early return decides what is painted, not what is
   * fetched, and every effect below was registered before it ran.
   */
  const canManageRisk = allowed('players.restrict');
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [alerts, setAlerts] = useState<RiskAlertRow[]>([]);
  const [rules, setRules] = useState<RiskRuleRow[]>([]);
  const [cases, setCases] = useState<RiskCaseRow[]>([]);
  /* Counted by the server over every case in scope, not from `cases` above —
     that list is capped at 50 and a KPI counted from it goes quietly wrong at
     the fifty-first. */
  const [caseSummary, setCaseSummary] = useState<CaseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingRules, setLoadingRules] = useState(true);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  /*
   * WHAT THE SERVER SAYS, rather than what this page happens to hold.
   *
   * Every figure beside the queue used to be counted from the loaded array.
   * That was near enough while the endpoint returned the whole queue in one
   * go; it stopped being true the moment the queue outgrew the window, and it
   * would be plainly wrong over a page of 25. `meta` is the page, `summary` is
   * the whole filtered set, and neither is derived here.
   */
  const [meta, setMeta] = useState({ page: 1, perPage: PER_PAGE, total: 0, totalPages: 1 });
  const [summary, setSummary] = useState<AlertSummary>({
    open: 0,
    criticalOpen: 0,
    byStatus: {},
    heldWithdrawals: 0,
    heldPlayers: 0,
  });

  /* The search reaches the server now, so it is debounced: a keystroke per
     request would put twenty queries behind a typed reference. */
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  /* Any change of what is being asked for returns to the first page. Staying on
     page 12 of a search that yields two rows shows an empty list and a pager
     that says so, which reads as "no results" for a search that has plenty. */
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, status, brandId]);

  /** Deep link: #/risk/RSK-1092 selects that alert on load. The ref is used
      rather than the uuid because the ref is what an operator pastes into a
      chat message. */
  const [selectedRef, setSelectedRef] = useState<string | null>(() => {
    const match = /^#\/risk\/([A-Za-z0-9-]+)$/.exec(globalThis.location.hash);
    return match ? match[1]! : null;
  });

  const [dialog, setDialog] = useState<'thresholds' | 'export' | null>(null);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  /* The control a Restricted verdict applies. Defaulted to the one the design's
     own copy describes — blocking withdrawals — but always shown, never
     applied silently. */
  const [restrictionKind, setRestrictionKind] =
    useState<StaffRestrictionKind>('WITHDRAWAL_BLOCK');
  const [restrictionReason, setRestrictionReason] =
    useState<RestrictionReasonCode>('RISK_REVIEW');
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | undefined>(undefined);
  const [closing, setClosing] = useState(false);

  /* ── The case file ───────────────────────────────────────────────────────
     `/api/risk/cases` returns a COUNT of findings; a case is worth nothing to
     an investigator as a number, so the selected player's case is fetched whole
     the moment it is known. Kept beside the alert rather than inside a dialog
     because the findings are what an operator reads BEFORE deciding anything. */
  const [caseFile, setCaseFile] = useState<RiskCaseFile | null>(null);
  const [loadingCase, setLoadingCase] = useState(false);
  const [caseAction, setCaseAction] = useState<'finding' | 'resolve' | 'open' | null>(null);
  const [caseText, setCaseText] = useState('');
  const [caseTextError, setCaseTextError] = useState<string | undefined>(undefined);
  const [caseVerdict, setCaseVerdict] = useState<CaseVerdict>('CLEARED');
  const [savingCase, setSavingCase] = useState(false);

  const [exportScope, setExportScope] = useState<string>(EXPORT_SCOPES[0]);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exportDateError, setExportDateError] = useState<string | undefined>(undefined);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState<{
    ref: string;
    rowCount: number;
    truncated: boolean;
    downloadPath: string;
  } | null>(null);

  /**
   * TAKING THE ALERTS OFF THE PLATFORM NEEDS BOTH KEYS.
   *
   * `players.restrict` is what READING an alert needs — it gates the list this
   * screen is built on — and `audit.export` is what taking a file off the
   * platform needs, everywhere. A file is not a lesser form of a read, so the
   * export asks for both and the route enforces both.
   */
  const canExportAlerts = allowed('players.restrict') && allowed('audit.export');

  const runAlertExport = useCallback(async () => {
    if (!exportFrom || !exportTo) {
      setExportDateError('Give the export a first and last day.');
      return;
    }
    if (exportFrom > exportTo) {
      setExportDateError('The range starts after it ends.');
      return;
    }
    setExportDateError(undefined);
    setExporting(true);
    try {
      const result = await api.post<{
        ref: string;
        rowCount: number;
        truncated: boolean;
        downloadPath: string;
      }>('/api/risk/alerts/export', {
        from: exportFrom,
        to: exportTo,
        ...(brandId ? { brandId } : {}),
        ...(exportScope === EXPORT_SCOPES[0] ? { status: 'OPEN' } : {}),
      });
      setExported(result);
      toast(
        result.truncated
          ? `${result.ref} is ready, and it is SHORT: the row cap stopped it at ${formatCount(result.rowCount)}. Narrow the window before you rely on it.`
          : `${result.ref} is ready — ${formatCount(result.rowCount)} ${result.rowCount === 1 ? 'alert' : 'alerts'}.`,
      );
    } catch (error: unknown) {
      showError({
        title: 'The export did not run',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'No file was produced. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setExporting(false);
    }
  }, [exportFrom, exportTo, exportScope, brandId, showError, toast]);

  /* ── Rule configuration ──────────────────────────────────────────────────
     One editor serves both writes. `mode` is which of the two endpoints the
     Save button will call, so a half-typed threshold and a half-typed
     disable-reason can never be posted to each other. */
  const [ruleEdit, setRuleEdit] = useState<{ id: string; mode: 'threshold' | 'state' } | null>(
    null,
  );
  const [thresholdDraft, setThresholdDraft] = useState<Record<string, string>>({});
  const [ruleReason, setRuleReason] = useState(RULE_REASONS[0]!.key);
  const [ruleNote, setRuleNote] = useState('');
  const [ruleFieldErrors, setRuleFieldErrors] = useState<Record<string, string>>({});
  const [savingRule, setSavingRule] = useState(false);

  useEffect(() => {
    /*
     * NOT ASKED. The module's refusal below is a RENDER decision that runs after
     * every hook here has been registered, so without this the read fired for a
     * role the route refuses and answered it with a modal claiming a failure.
     */
    if (!canManageRisk) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    if (status !== 'ALL') params.set('status', status);
    if (debouncedQuery) params.set('q', debouncedQuery);
    params.set('page', String(page));
    params.set('perPage', String(PER_PAGE));
    const qs = params.toString();

    api
      .get<{ items: RiskAlertRow[]; meta: typeof meta; summary: AlertSummary }>(
        `/api/risk/alerts?${qs}`,
      )
      .then((d) => {
        if (cancelled) return;
        setAlerts(d.items);
        setMeta(d.meta);
        setSummary(d.summary);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        showError({
          title: 'Could not load the risk queue',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canManageRisk, brandId, status, debouncedQuery, page, showError]);

  /*
   * Rules and cases are NOT brand-scoped by the API, so they load once and are
   * labelled "all brands" wherever they are counted.
   *
   * Extracted from the effect because the rule-book writes below have to
   * RE-READ it. A threshold edit or a state change moves the row's own cells
   * and, on a disable, the sentence under the table about how many alerts the
   * rule left open — patching the one row in place would leave the rest of the
   * panel describing the rule book as it was a moment ago.
   */
  const loadRuleBook = useCallback(() => {
    /* Guarded HERE as well as at the effect that calls it on mount. The buttons
       that also call it are only reachable once the module has rendered, so they
       were never the risk -- but a callback that refuses for itself cannot be
       un-guarded later by an edit to its caller. */
    if (!canManageRisk) {
      setLoadingRules(false);
      return Promise.resolve();
    }
    setLoadingRules(true);
    return Promise.all([
      api.get<{ items: RiskRuleRow[] }>('/api/risk/rules'),
      api.get<{ items: RiskCaseRow[]; summary: CaseSummary }>('/api/risk/cases'),
    ])
      .then(([r, c]) => {
        setRules(r.items);
        setCases(c.items);
        setCaseSummary(c.summary);
      })
      .catch((error: unknown) => {
        showError({
          title: 'Could not load the rule book',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => setLoadingRules(false));
  }, [canManageRisk, showError]);

  useEffect(() => {
    /*
     * NOT ASKED. The module's refusal below is a RENDER decision that runs after
     * every hook here has been registered, so without this the read fired for a
     * role the route refuses and answered it with a modal claiming a failure.
     */
    if (!canManageRisk) return;
    void loadRuleBook();
  }, [canManageRisk, loadRuleBook]);

  /*
   * NO CLIENT-SIDE FILTERING ANY MORE.
   *
   * `searched` and `visible` used to narrow the loaded array, and the notice
   * under the list explained that they only ever saw what had been loaded. The
   * server now applies the search and the status chip before the page is cut,
   * so `alerts` IS the visible set — 456 open alerts are reachable where 100
   * were, and a reference pasted out of a chat message is found whichever page
   * it lives on.
   *
   * `visible` is kept as an alias rather than renamed throughout: it is read in
   * several places below, and one missed rename would leave a figure quietly
   * counting the wrong population, which is the exact defect this change exists
   * to remove. `searched` is gone — the chip badges read the server's own
   * per-status counts now, which is the only thing it was still used for.
   */
  const visible = alerts;

  // "If the selected alert falls out of scope, the first in-scope alert is
  // shown" — which is this fallback, not an effect that resets state.
  /*
   * A DEEP LINK MUST NOT SILENTLY SELECT A DIFFERENT ALERT.
   *
   * This resolved the ref against the loaded rows and fell back to the first
   * one. That was harmless while the screen held the whole queue: a ref that
   * existed was always in it. Paged, an alert on page 8 is not on page 1, so
   * `#/risk/RSK-1713` quietly opened RSK-1711 instead — the operator reads a
   * case file, closes an alert, and it is not the alert they were sent.
   *
   * So a ref that is not on this page is FETCHED. The fallback to the first row
   * stays for the no-deep-link case, and only for it.
   */
  const [deepLinked, setDeepLinked] = useState<{ ref: string; row: RiskAlertRow | null } | null>(
    null,
  );
  useEffect(() => {
    /*
     * ONLY WHILE NOTHING IS FILTERED, and that distinction is the whole rule.
     *
     * A ref missing from the rows can mean two opposite things. With no filter
     * on, it is on another page and should be fetched — that is what this
     * lookup is for. With a search or a status chip active, it was EXCLUDED,
     * and fetching it would put a case file on screen that the queue beside it
     * deliberately no longer offers: an operator narrows to one rule, and the
     * record they are reading is from a different one.
     *
     * A deep link always arrives with no filter set, so the case this exists
     * for is unaffected.
     */
    const filtering = debouncedQuery !== '' || status !== 'ALL';
    /* `!canManageRisk` is the fourth reason, and the only one that is about the
       OPERATOR rather than the query: the refusal below paints a notice but does
       not stop this hook, so the read went out and was answered 403. */
    if (!selectedRef || !canManageRisk || filtering || alerts.some((a) => a.ref === selectedRef)) {
      setDeepLinked(null);
      return;
    }
    let cancelled = false;
    /* SCOPED TO THE SELECTED BRAND, exactly like the queue it stands in for.
       Without brandId this searched the operator's whole scope and happily
       returned an alert the brand filter had just excluded — so switching
       brands kept the old case file on screen, under a queue that no longer
       contained it. */
    const lookup = new URLSearchParams({ q: selectedRef });
    if (brandId) lookup.set('brandId', brandId);
    api
      .get<{ items: RiskAlertRow[] }>(`/api/risk/alerts?${lookup.toString()}`)
      .then((d) => {
        if (cancelled) return;
        /* The ref is RECORDED alongside the row, including when the row is
           null. Without it "not looked up yet" and "looked up and not there"
           are the same value, and the screen would show nothing at all while
           the request was in flight. */
        setDeepLinked({ ref: selectedRef, row: d.items.find((a) => a.ref === selectedRef) ?? null });
      })
      .catch(() => !cancelled && setDeepLinked({ ref: selectedRef, row: null }));
    return () => {
      cancelled = true;
    };
  }, [canManageRisk, selectedRef, alerts, brandId, debouncedQuery, status]);

  /*
   * WHICH ALERT THE CASE FILE SHOWS, in four cases and no fewer.
   *
   *   on this page          → that row
   *   filtered out          → the first row still in scope. The operator
   *                           narrowed the queue; holding a record it no longer
   *                           offers is the defect this fallback exists for
   *   looked up and found   → that row, fetched from another page
   *   looked up and missing → the first row, same reason as "filtered out"
   *   lookup in flight      → nothing yet, rather than flashing the wrong case
   *                           file on the way to the right one
   */
  const filtering = debouncedQuery !== '' || status !== 'ALL';
  const onThisPage = visible.find((a) => a.ref === selectedRef);
  const lookupSettled = deepLinked?.ref === selectedRef;
  const selected =
    onThisPage ??
    (!selectedRef || filtering || lookupSettled
      ? (deepLinked?.row ?? visible[0] ?? null)
      : null);

  useEffect(() => {
    if (!selected) return;
    const want = `#/risk/${selected.ref}`;
    // replaceState, not `location.hash = …`: assigning the hash pushes a history
    // entry that react-router's BrowserRouter never sees, and selecting an alert
    // is not a navigation the back button should have to unwind.
    if (globalThis.location.hash !== want) globalThis.history.replaceState(null, '', want);
  }, [selected]);

  const brandLabel = brands.find((b) => b.id === brandId)?.name ?? 'All brands';
  /* FROM THE SERVER, over the whole filtered set. Counted here they would have
     described one page of 25 and been captioned as though they described the
     queue. `notCleared` is the queue minus what has been cleared, which the
     status breakdown gives without a second query. */
  const openCount = summary.open;
  const criticalOpen = summary.criticalOpen;
  const notCleared = meta.total - (summary.byStatus['CLEARED'] ?? 0);
  /* Sits in the same caption as notCleared, so it has to describe the same
     population. The server counts CRITICAL over the brand scope; anything
     narrower would put a page-sized number beside a queue-sized one. */
  const criticalInScope = summary.criticalOpen;

  /**
   * Held withdrawals, deduplicated BY PLAYER — in SQL now, not here.
   *
   * `pending_withdrawals` is a per-player count repeated on every one of that
   * player's alerts, so summing the column reports two held withdrawals where
   * one player has two alerts and one withdrawal. The Map that deduplicated it
   * could only ever see the rows on this page; the same DISTINCT now happens
   * over the whole filtered set, which is the population the caption claims.
   */
  const held = { count: summary.heldWithdrawals, players: summary.heldPlayers };

  const firedTotal = rules.reduce((sum, r) => sum + Number(r.fired_30d), 0);

  /**
   * Counted over the SEARCH RESULT, not over everything loaded.
   *
   * The badge is a promise about what the chip will produce. Counting the whole
   * queue while a search is narrowing the list puts "Open 5" directly above two
   * rows — a reconciliation break an operator has to resolve by hand, on the
   * one screen whose whole job is deciding whether a number can be trusted.
   */
  /* The badge is a promise about what the chip will produce, so it is counted
     over the SEARCH result and ignores whichever chip is currently selected —
     otherwise every badge but the active one reads zero. The server counts it
     that way; this only reads it. */
  const statusChips = STATUS_CHIPS.map((c) => ({
    key: c.key,
    label: c.label,
    count:
      c.key === 'ALL'
        ? Object.values(summary.byStatus).reduce((n, v) => n + v, 0)
        : (summary.byStatus[c.key] ?? 0),
  }));

  /**
   * Rule 4: "case files aggregate every alert for a player."
   *
   * FETCHED, NOT FILTERED. This read the loaded array, which held the whole
   * queue when the queue was small enough to load. Over a page of 25 it would
   * have shown the player's alerts THAT HAPPEN TO SHARE THIS PAGE and called
   * them every alert for that player — the case file's one job is to be
   * complete, so a partial list under that heading is worse than none.
   *
   * The player's reference is the search term because the server matches it,
   * so this is the same filter the operator could type by hand.
   */
  const [playerAlerts, setPlayerAlerts] = useState<RiskAlertRow[]>([]);
  const selectedPlayerRef = selected?.player_ref ?? null;
  useEffect(() => {
    if (!selectedPlayerRef || !canManageRisk) {
    /*
     * NOT ASKED. The module's refusal below is a RENDER decision that runs after
     * every hook here has been registered, so without this the read fired for a
     * role the route refuses and answered it with a modal claiming a failure.
     */
      setPlayerAlerts([]);
      return;
    }
    let cancelled = false;
    api
      .get<{ items: RiskAlertRow[] }>(
        `/api/risk/alerts?q=${encodeURIComponent(selectedPlayerRef)}&perPage=200`,
      )
      .then((d) => !cancelled && setPlayerAlerts(d.items))
      /* A case file that cannot list the player's other alerts shows none
         rather than a subset — see the note above. The queue itself is
         unaffected, so this does not raise a modal over a working screen. */
      .catch(() => !cancelled && setPlayerAlerts([]));
    return () => {
      cancelled = true;
    };
  }, [canManageRisk, selectedPlayerRef]);
  // risk_alert has no case_id and risk_case has no alert list, so the case can
  // only be matched through the player. Labelled as inferred where it is shown.
  const playerCase = selected ? cases.find((c) => c.player_ref === selected.player_ref) : undefined;
  const playerCaseId = playerCase?.id ?? null;

  /*
   * The selected player's case, whole.
   *
   * Keyed on the case ID so switching between two alerts on the SAME player
   * does not refetch, and switching to a different player's case does. The
   * findings are cleared before the request rather than after it: showing the
   * previous case's evidence under the new case's header for the length of a
   * round trip is the one mistake this panel must never make.
   */
  useEffect(() => {
    if (!playerCaseId || !canManageRisk) {
    /*
     * NOT ASKED. The module's refusal below is a RENDER decision that runs after
     * every hook here has been registered, so without this the read fired for a
     * role the route refuses and answered it with a modal claiming a failure.
     */
      setCaseFile(null);
      return;
    }
    let cancelled = false;
    setCaseFile(null);
    setLoadingCase(true);
    api
      .get<RiskCaseFile>(`/api/risk/cases/${playerCaseId}`)
      .then((d) => !cancelled && setCaseFile(d))
      .catch((error: unknown) => {
        if (cancelled) return;
        showError({
          title: 'Could not open the case file',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoadingCase(false));
    return () => {
      cancelled = true;
    };
  }, [canManageRisk, playerCaseId, showError]);

  function openVerdict(next: Verdict) {
    setVerdict(next);
    setNote('');
    setNoteError(undefined);
  }

  function openCaseAction(next: 'finding' | 'resolve' | 'open') {
    setCaseAction(next);
    setCaseText('');
    setCaseTextError(undefined);
    setCaseVerdict('CLEARED');
  }

  /**
   * The three case writes, behind one submit.
   *
   * All three take a written sentence and all three refuse a short one — the
   * same ten-character floor the contract sets and the `risk_case_note_body_
   * said_something` CHECK enforces. It is caught here so the operator does not
   * lose what they typed to a round trip, and the server refuses it anyway.
   */
  async function submitCaseAction() {
    if (!caseAction || !selected) return;
    const body = caseText.trim();
    if (body.length < 10) {
      setCaseTextError(
        caseAction === 'resolve'
          ? 'Say what the verdict rests on — at least 10 characters.'
          : 'Write what you found — at least 10 characters. Somebody else reads this case.',
      );
      return;
    }

    setSavingCase(true);
    try {
      if (caseAction === 'open') {
        const made = await api.post<{ ref: string }>('/api/risk/cases', {
          playerId: selected.player_id,
          summary: body,
        });
        toast(`Case ${made.ref} opened on ${selected.player ?? 'this player'}`);
      } else if (playerCaseId && caseAction === 'finding') {
        await api.post(`/api/risk/cases/${playerCaseId}/findings`, { body });
        toast(`Finding recorded on ${playerCase?.ref ?? 'the case'}`);
      } else if (playerCaseId) {
        const done = await api.post<{ ref: string; verdict: string }>(
          `/api/risk/cases/${playerCaseId}/resolve`,
          { verdict: caseVerdict, note: body },
        );
        toast(`${done.ref} closed as ${done.verdict.toLowerCase()}`);
      }
      setCaseAction(null);
      setCaseText('');
      /* Re-read rather than patch: the case list carries the status and the
         finding count this write just moved, and the panel above reads both. */
      await loadRuleBook();
      if (playerCaseId) {
        const refreshed = await api.get<RiskCaseFile>(`/api/risk/cases/${playerCaseId}`);
        setCaseFile(refreshed);
      }
    } catch (error: unknown) {
      showError({
        title:
          caseAction === 'resolve'
            ? 'Could not resolve the case'
            : caseAction === 'open'
              ? 'Could not open the case'
              : 'Could not record the finding',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
      });
    } finally {
      setSavingCase(false);
    }
  }

  async function submitClose() {
    if (!selected || !verdict) return;
    const body = note.trim();
    // Mirrors the server's own `note: z.string().min(3)` and, behind it, the
    // risk_alert_closed_has_note check. Caught here so the operator does not
    // lose the dialog to a round-trip.
    if (body.length < 3) {
      setNoteError('Write a closing note — at least 3 characters. A verdict without one is rejected.');
      return;
    }
    setClosing(true);
    try {
      const result = await api.post<{
        ref: string;
        verdict: string;
        restriction: { kind: string; alreadyInForce: boolean } | null;
        case: { ref: string | null; opened: boolean; reason?: string } | null;
        withdrawalHoldsLifted: string[];
      }>(`/api/risk/alerts/${selected.id}/close`, {
        verdict,
        note: body,
        ...(verdict === 'RESTRICTED'
          ? { restriction: { kind: restrictionKind, reasonCode: restrictionReason } }
          : {}),
      });
      const closedAt = new Date().toISOString();
      setAlerts((cur) =>
        cur.map((a) =>
          a.id === selected.id
            ? {
                ...a,
                status: result.verdict,
                closing_note: body,
                closed_at: closedAt,
                closed_by: session?.displayName ?? 'You',
              }
            : a,
        ),
      );
      /*
       * The toast reports WHAT HAPPENED, not what the verdict is called.
       *
       * Three different outcomes are possible behind one button and the
       * operator cannot see any of them from the queue: the escalation opened a
       * case or joined one that already existed, the close lifted a withdrawal
       * hold or there was none to lift, and a Restricted verdict may have found
       * the control already in force. Each is named from the RESPONSE — never
       * assumed from the verdict, which is the mistake the prototype's toasts
       * made when they announced a released hold and a drafted SAR.
       */
      const lifted = result.withdrawalHoldsLifted ?? [];
      const holdLine =
        lifted.length > 0
          ? ` · ${formatCount(lifted.length)} withdrawal${lifted.length === 1 ? '' : 's'} back in the checker queue`
          : '';
      toast(
        result.restriction?.alreadyInForce
          ? `${result.ref} closed as restricted · ${selected.player ?? 'the player'} was already under that control${holdLine}`
          : result.case?.ref
            ? `${result.ref} escalated · ${result.case.opened ? 'case' : 'added to case'} ${result.case.ref}${holdLine}`
            : `${CLOSE_TOAST[verdict](result.ref, selected.player ?? 'the player')}${holdLine}`,
      );
      /* Escalating opens or appends to a case, and closing may have moved
         withdrawals: both live in panels loaded elsewhere, so both are re-read
         rather than guessed at locally. */
      void loadRuleBook();
      setVerdict(null);
      setNote('');
    } catch (error: unknown) {
      showError({
        title: `Could not close ${selected.ref}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The verdict was not recorded. Try again shortly.',
      });
    } finally {
      setClosing(false);
    }
  }


  /* ── Rule configuration ──────────────────────────────────────────────────
     §16. Reading the rule book AND changing it are both players.restrict, the
     capability this whole screen is gated on.

     Changing it used to need access.manage. That is "manage roles & users" and
     only Super Admin holds it, so the Risk Officer — the person whose job the
     rule book is — could read every threshold and move none of them, and this
     screen rendered a Configure column it hid from every role that could open
     it. Tuning what raises an alert is the same authority as acting on one:
     both are players.restrict now, so there is one capability on this screen
     rather than a second one nobody who works here has. */

  /*
   * The rule book grid, with the two writes.
   *
   * No capability branch on the column any more: reaching this line already
   * means the role holds players.restrict — the early return above proves it —
   * and a second check against the same key would be a gate that can only ever
   * be open, which is the kind of dead branch that later drifts into a lie.
   */
  const ruleBookColumns: Column<RiskRuleRow>[] = [
    ...thresholdColumns,
    {
      key: 'configure',
      header: 'Configure',
      render: (r) => {
        const shape = THRESHOLD_SHAPE[r.code];
        return (
          <span className="row">
            <Button
              onClick={() => openRuleEditor(r, 'threshold')}
              /* No shape is defined for this rule, so there is nothing to
                 validate a value against. Disabled and explained rather
                 than offered and refused by the server. */
              disabled={!shape || savingRule}
              aria-describedby={shape ? undefined : 'threshold-gap'}
            >
              Edit threshold
            </Button>
            <Button onClick={() => openRuleEditor(r, 'state')} disabled={savingRule}>
              {r.enabled ? 'Disable' : 'Enable'}
            </Button>
          </span>
        );
      },
    },
  ];

  const editingRule = ruleEdit ? (rules.find((r) => r.id === ruleEdit.id) ?? null) : null;
  const editingShape = editingRule ? (THRESHOLD_SHAPE[editingRule.code] ?? null) : null;

  function openRuleEditor(rule: RiskRuleRow, mode: 'threshold' | 'state') {
    setRuleEdit({ id: rule.id, mode });
    // Seeded from what is STORED, so an edit starts from the live threshold
    // rather than from an empty box the operator has to retype from the row.
    const shape = THRESHOLD_SHAPE[rule.code] ?? [];
    const draft: Record<string, string> = {};
    for (const field of shape) {
      const current = (rule.config ?? {})[field.key];
      draft[field.key] = current === undefined || current === null ? '' : String(current);
    }
    setThresholdDraft(draft);
    setRuleReason(mode === 'state' && rule.enabled ? 'RULE_RETIRED' : RULE_REASONS[0]!.key);
    setRuleNote('');
    setRuleFieldErrors({});
  }

  function closeRuleEditor() {
    setRuleEdit(null);
    setThresholdDraft({});
    setRuleNote('');
    setRuleFieldErrors({});
  }

  /** The note both writes require. Mirrors the contract's `min(10)` exactly. */
  function noteProblem(): string | null {
    return ruleNote.trim().length < 10
      ? 'Say why it is changing — at least 10 characters. A reason code on its own reads as a form number six months later.'
      : null;
  }

  async function saveRuleThreshold() {
    if (!editingRule || !editingShape) return;
    const errors: Record<string, string> = {};
    const config: Record<string, number> = {};

    for (const field of editingShape) {
      const raw = (thresholdDraft[field.key] ?? '').trim();
      // Integers only, and the same bounds the contract enforces — a threshold
      // is counted, and 4.5 deposits is not a number of deposits.
      if (!/^\d+$/.test(raw)) {
        errors[field.key] = `${field.label} is a whole number between ${field.min} and ${field.max}.`;
        continue;
      }
      const value = Number(raw);
      if (value < field.min || value > field.max) {
        errors[field.key] = `${field.label} must be between ${field.min} and ${field.max}.`;
        continue;
      }
      config[field.key] = value;
    }

    const noteFault = noteProblem();
    if (noteFault) errors['note'] = noteFault;
    if (Object.keys(errors).length > 0) {
      setRuleFieldErrors(errors);
      return;
    }

    setSavingRule(true);
    try {
      await api.post<{ code: string; config: Record<string, number> }>(
        `/api/risk/rules/${editingRule.id}/threshold`,
        { code: editingRule.code, config, reasonCode: ruleReason, note: ruleNote.trim() },
      );
      // Re-read rather than patch: the threshold column, the version and the
      // fire counts all come off the same endpoint.
      await loadRuleBook();
      /* The rule's name, never its code (VELOCITY_DEPOSIT): the code is what the platform files it
         under, and the name is what the grid shows the operator. */
      toast(
        `${editingRule.name} threshold saved · applies to new events only, closed alerts keep their verdicts`,
      );
      closeRuleEditor();
    } catch (error: unknown) {
      showError({
        title: `Could not change the ${editingRule.name} threshold`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The threshold was not changed. Try again shortly.',
      });
    } finally {
      setSavingRule(false);
    }
  }

  async function saveRuleState() {
    if (!editingRule) return;
    const noteFault = noteProblem();
    if (noteFault) {
      setRuleFieldErrors({ note: noteFault });
      return;
    }
    const next = !editingRule.enabled;

    setSavingRule(true);
    try {
      const result = await api.post<{ enabled: boolean; openAlerts: number }>(
        `/api/risk/rules/${editingRule.id}/state`,
        { enabled: next, reasonCode: ruleReason, note: ruleNote.trim() },
      );
      await loadRuleBook();
      toast(
        next
          ? `${editingRule.name} enabled · it can raise alerts again`
          : `${editingRule.name} disabled · it raises no new alerts, and ${formatCount(
              result.openAlerts,
            )} open alert${result.openAlerts === 1 ? '' : 's'} stay open for a verdict`,
      );
      closeRuleEditor();
    } catch (error: unknown) {
      showError({
        title: `Could not ${next ? 'enable' : 'disable'} ${editingRule.name}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The rule was not changed. Try again shortly.',
      });
    } finally {
      setSavingRule(false);
    }
  }

  if (!canManageRisk) {
    return (
      <EmptyState icon="lock-key" title="Risk management">
        This module needs the players.restrict capability — the same one reading and closing
        alerts needs everywhere. Your role does not hold it.
      </EmptyState>
    );
  }

  const { title: emptyTitle, hint: emptyHint } = emptyStateFor({
    status,
    query,
    brandLabel: brandId ? brandLabel : null,
  });

  return (
    <>
      <ScreenHeader
        /* "Risk management", not the sidebar's "Risk". The rail shortens it to
           fit a narrow column; the screen itself has always said the longer
           form, and so does the capability refusal above — an operator who is
           told "this module needs the restrict capability" under the heading
           "Risk management" and then gets in has to land on the same words, or
           the two paths look like two different places. */
        title="Risk management"
        /* The sentence the module header comment at the top of this file has
           been making to developers since it was written, finally said to the
           person doing the work: alerts are rule output, and this screen is
           where somebody turns each one into a verdict they signed. The notice
           below it goes on to say what the three verdicts are and that none of
           them can be skipped — the purpose says what the screen is FOR, the
           notice says how it refuses to be short-cut. */
        purpose="Where a risk officer works down the alerts the rules raised and settles each one as cleared, restricted or escalated, with a written note."
        /*
          The count line exactly as it was, both halves, same wording.
          `formatCount` and the singular/plural switch are kept because they are
          the reason the line reads as a sentence rather than a readout.
          Pluralisation on `openCount` only: "1 open alert · 1 critical" is
          correct as written, since the second figure is a count qualifying the
          first and takes no noun of its own.

          "Loading…" means the queue read has not come back yet, and it is NOT
          interchangeable with a zero — a zero here is the platform answering
          that nothing is open, which on this screen is the finding, and the
          figure a risk officer would act on.

          There is no third, read-failed state in this caption, and that is
          deliberate rather than forgotten: when `GET /api/risk/alerts` fails the
          screen raises a modal titled "Could not load the risk queue", so the
          failure is already in front of the operator and unmissable rather than
          being whispered in a caption behind it. The brand label is appended
          unconditionally because it comes from the filter the operator set, not
          from a read that can fail — it is true even while the queue is still
          loading.
        */
        meta={
          <>
            {loading
              ? 'Loading…'
              : `${formatCount(openCount)} open alert${openCount === 1 ? '' : 's'} · ${formatCount(criticalOpen)} critical`}{' '}
            · {brandLabel}
          </>
        }
        actions={
          <>
            <Button onClick={() => setDialog('thresholds')}>
              <Icon name="sliders-horizontal" />
              Rule thresholds
            </Button>
            <Button variant="primary" onClick={() => setDialog('export')}>
              <Icon name="export" />
              Risk report
            </Button>
          </>
        }
      />

      <div className="notice notice-quiet">
        <Icon name="shield-warning" />
        <div>
          {/* NO CONSTRAINT NAME. This read "the database enforces it —
              risk_alert_closed_has_note rejects any closed alert without a note
              and a closer". The reassurance is worth keeping and the identifier
              is not: it tells a risk officer nothing they can act on, and the
              same sentence without it says the same thing. */}
          Alerts are rule output, not verdicts: every one is closed as{' '}
          <strong>Cleared</strong>, <strong>Restricted</strong> or <strong>Escalated</strong>, with
          a note. A close without a note is refused — not by this screen, but by the platform
          itself, so it cannot be got round. Your name goes on it, and it cannot be undone or
          closed twice.
        </div>
      </div>

      <div className="stat-grid">
        <Stat
          label="Alerts in scope"
          /* meta.total, NOT the page. "Alerts in scope" is a claim about the
             queue; rendering the page length under that label turned 456 into
             50 the moment paging landed, which is precisely the figure this
             screen exists to be trusted on. Caught by the e2e spec, which
             checks the page count and the scope count separately. */
          value={formatCount(meta.total)}
          delta={`${formatCount(notCleared)} not cleared · ${formatCount(criticalInScope)} critical`}
          deltaUp={criticalInScope > 0}
        />
        <Stat
          label="Withdrawals held"
          // A count, never a peso figure: the alerts endpoint returns the number
          // of pending withdrawals per player and no amount at all.
          value={formatCount(held.count)}
          delta={
            held.count === 0
              ? 'none held'
              : `${formatCount(held.players)} player${held.players === 1 ? '' : 's'} · count only, no amount`
          }
          deltaUp={held.count > 0}
        />
        <Stat
          label="Rules fired 30d"
          value={loadingRules ? '—' : formatCount(firedTotal)}
          delta="alerts raised · all brands"
        />
        {/* WAS: "Not tracked — no confirmed-fraud figure exists". That was false.
            A case's verdict IS its status, and `risk_case_verdict` is a CHECK
            over OPEN · CONFIRMED · CLEARED · INCONCLUSIVE, so the figure was
            always derivable — the screen simply never asked for it. Declining
            to show a number you can support reads as policy, and a reader stops
            asking. Zero confirmed is a finding; "not tracked" is not. */}
        <Stat
          label="Confirmed fraud 30d"
          value={caseSummary === null ? '—' : formatCount(caseSummary.confirmed30d)}
          delta={
            caseSummary === null
              ? 'reading…'
              : `${formatCount(caseSummary.cleared30d)} cleared · ${formatCount(
                  caseSummary.openNow,
                )} still open`
          }
          deltaUp={caseSummary !== null && caseSummary.confirmed30d > 0}
        />
      </div>

      {/*
       * The two statuses counted here are `AWAITING_CHECKER` and `HELD_BY_RISK`. An operator
       * has neither name in front of them: they have the two chips on the withdrawals queue,
       * labelled "Awaiting checker" and "Held by risk", which select exactly the rows this
       * figure counts — so the sentence names the chips and sends them somewhere they can
       * check it. The `.tabular` spans went with the names; that markup marks a literal the
       * reader is looking at, and a status constant is not one.
       */}
      <RiskFiguresNotice />

      <div className="split">
        {/* ── Left: the queue ─────────────────────────────────────────────── */}
        <div className="stack">
          <div className="search-field" style={{ width: '100%', maxWidth: '420px' }}>
            <Icon name="magnifying-glass" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Alert ID, player or rule"
              aria-label="Search risk alerts"
            />
          </div>

          <div className="row">
            <span className="panel-title">Status</span>
            <Chips
              options={statusChips}
              active={status}
              onChange={setStatus}
              label="Filter risk alerts by status"
            />
          </div>

          {/* The whole filtered queue, not this page — same reason as the KPI
              above. The pager beneath the list says which slice is on screen. */}
          <Panel title={`Alerts · ${formatCount(meta.total)} in view`}>
            {loading && alerts.length === 0 ? (
              <div className="skeleton" style={{ height: '260px' }}>
                &nbsp;
              </div>
            ) : visible.length === 0 ? (
              <EmptyState icon="shield-check" title={emptyTitle}>
                {emptyHint}
              </EmptyState>
            ) : (
              <div className="stack" role="group" aria-label="Risk alerts">
                {visible.map((a) => {
                  const current = a.id === selected?.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className={current ? 'queue-pill attention' : 'queue-pill'}
                      aria-current={current ? true : undefined}
                      // The accent tint is the selection, paired with the ring
                      // AND with aria-current — never colour on its own.
                      style={current ? { background: 'var(--portal-accent-tint)' } : undefined}
                      onClick={() => setSelectedRef(a.ref)}
                    >
                      <span className="row">
                        <Tag tone={severityTone(a.severity)}>
                          {SEVERITY_LABEL[a.severity] ?? a.severity}
                        </Tag>
                        <span className="text-subtle" style={{ fontSize: '11.5px' }}>
                          {relative(a.created_at)}
                        </span>
                        <span className="header-spacer">
                          <Tag tone={statusTone(a.status)}>
                            {STATUS_LABEL[a.status] ?? a.status}
                          </Tag>
                        </span>
                      </span>
                      <span
                        className="queue-pill-value"
                        style={{ fontSize: '15px', display: 'block' }}
                      >
                        {a.rule}
                      </span>
                      <span className="queue-pill-meta">
                        {a.player ?? 'no player'} · {a.player_ref ?? '—'} ·{' '}
                        <span className="tabular">{a.ref}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <Pagination
              numbered
              page={meta.page}
              perPage={meta.perPage}
              total={meta.total}
              onPage={setPage}
            />

            {/* WAS: "Search and the status chips run over the alerts already
                loaded — the endpoint takes only a brand and returns at most
                100." Both halves are now false, which is the point of the
                change: the search and the chips are applied by the server
                across every alert, and the pager reaches all of them. The
                sentence about the missing chip is still true and stays. */}
            {/*
                The column is `risk_alert.status`, and the sentence below no longer says so. The
                reader has the chips above it instead, and those are the whole set of statuses an
                alert can hold — which is the same fact, told in the words already on their
                screen. The `.tabular` span went with the name, because there is no longer a
                literal for it to mark. */}
            <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
              <Icon name="info" />
              <div>
                Search and the status chips are applied across the whole queue, not just this page,
                and every count beside the list is of the whole queue too. Ordered open first, then
                Critical to Low. There is no <em>Investigating</em> chip because an alert has no
                such status; an alert is Open until a verdict closes it.
              </div>
            </div>
          </Panel>

          <Panel title="Rules · fired 30d">
            {loadingRules && rules.length === 0 ? (
              <div className="skeleton" style={{ height: '160px' }}>
                &nbsp;
              </div>
            ) : (
              <DataTable
                caption="Risk rules with the number of alerts each raised in the last 30 days"
                columns={ruleColumns}
                rows={rules}
                rowKey={(r) => r.id}
                empty={
                  <EmptyState icon="list-checks" title="No rules configured">
                    The rule book is empty. Nothing is evaluating deposits, withdrawals or bonus
                    play, so an empty alert queue would mean nothing.
                  </EmptyState>
                }
              />
            )}
            {/*
                What is counted is rows of `risk_alert`. "Raised alerts" is the same count said
                in the reader's own terms — they are looking at the alert queue beside this —
                and it keeps the distinction the sentence exists to draw, between an alert being
                raised and a rule merely firing. The table name is here because a developer
                checking the count needs it; the operator could do nothing with it. */}
            <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
              <Icon name="warning-circle" />
              <div>
                &ldquo;Fired 30d&rdquo; is alerts raised, not rule fires — only raised alerts are
                counted, so a fire that only flags for review is not counted. There is no{' '}
                <em>Automatic action</em> column because a rule does not record one; the
                rule&rsquo;s own description is printed instead. These counts are not
                brand-scoped.{' '}
                <strong>Enabled is not armed:</strong> the Evaluated column says whether anything
                on this platform measures the rule at all.{' '}
                {rules.filter((r) => !r.evaluated).length > 0 ? (
                  <>
                    {/* The rule's name, as the grid shows it — never its code. */}
                    {rules
                      .filter((r) => !r.evaluated)
                      .map((r) => r.name)
                      .join(' and ')}{' '}
                    {rules.filter((r) => !r.evaluated).length === 1 ? 'is' : 'are'} not evaluated
                    by anything —{' '}
                    {rules
                      .filter((r) => !r.evaluated)
                      .map((r) => r.evaluation_detail)
                      .join(' ')}
                  </>
                ) : null}
              </div>
            </div>
          </Panel>
        </div>

        {/* ── Right: the case file ────────────────────────────────────────── */}
        <div className="stack">
          {!selected ? (
            <Panel>
              <EmptyState icon="shield-check" title="No alert selected">
                {/* The selection rides in the hash: #/risk/RSK-1092. */}
                Pick an alert on the left to open its case file. The address bar keeps the selection,
                so a copied link opens straight onto that alert.
              </EmptyState>
            </Panel>
          ) : (
            <>
              <Panel>
                <div className="row">
                  <Tag tone={severityTone(selected.severity, true)}>
                    {SEVERITY_LABEL[selected.severity] ?? selected.severity}
                  </Tag>
                  <strong>{selected.rule}</strong>
                  <Tag tone={statusTone(selected.status, true)}>
                    {STATUS_LABEL[selected.status] ?? selected.status}
                  </Tag>
                  <span className="text-subtle header-spacer" style={{ fontSize: '11.5px' }}>
                    <span className="tabular">{selected.ref}</span> ·{' '}
                    {relative(selected.created_at)}
                  </span>
                </div>

                <p style={{ maxWidth: '70ch', textWrap: 'pretty', fontSize: '13.5px' }}>
                  {detailSummary(selected.detail) ?? (
                    <span className="text-subtle">
                      This alert carries no summary — only the rule that fired and when.
                    </span>
                  )}
                </p>

                <div className="text-muted" style={{ fontSize: '13px' }}>
                  {selected.player ?? 'No player on this alert'}
                  {selected.player_ref ? ` · ${selected.player_ref}` : ''}
                  {selected.brand ? ` · ${selected.brand}` : ''}
                </div>

                {selected.status === 'OPEN' ? (
                  <div className="row" style={{ marginTop: 'var(--space-4)' }}>
                    <Button onClick={() => openVerdict('CLEARED')}>
                      <Icon name="check-circle" />
                      Clear alert
                    </Button>
                    <Button onClick={() => openVerdict('RESTRICTED')}>
                      <Icon name="prohibit" />
                      Restrict player
                    </Button>
                    <Button variant="primary" onClick={() => openVerdict('ESCALATED')}>
                      <Icon name="warning-octagon" />
                      Escalate to compliance
                    </Button>
                  </div>
                ) : (
                  <div className="notice notice-quiet" style={{ marginTop: 'var(--space-4)' }}>
                    <Icon name="lock-simple" />
                    <div>
                      Closed as <strong>{STATUS_LABEL[selected.status] ?? selected.status}</strong>
                      {selected.closed_by ? ` by ${selected.closed_by}` : ''}
                      {selected.closed_at ? ` · ${relative(selected.closed_at)}` : ''}.
                      {selected.closing_note ? ` “${selected.closing_note}”` : ''} A verdict is
                      final — re-closing would overwrite the original decision and its author, so
                      the platform refuses it.
                    </div>
                  </div>
                )}
              </Panel>

              <Panel title="Signals">
                <dl className="stack" style={{ margin: 0 }}>
                  <div className="def-row">
                    <dt>Player risk score</dt>
                    <dd>{selected.risk_score ?? '—'}</dd>
                  </div>
                  <div className="def-row">
                    <dt>Pending withdrawals</dt>
                    <dd>{formatCount(Number(selected.pending_withdrawals ?? '0'))}</dd>
                  </div>
                  <div className="def-row">
                    {/* The rule row is joined by id, so this is the version that
                        fired — not whatever the rule was later edited to. */}
                    <dt>Rule version at fire</dt>
                    <dd>
                      {selected.rule_code} v{selected.rule_version}
                    </dd>
                  </div>
                  {detailSignals(selected.detail).map((s) => (
                    <div className="def-row" key={s.label}>
                      <dt>{s.label}</dt>
                      <dd>{s.value}</dd>
                    </div>
                  ))}
                </dl>

                {/*
                   The job is `risk.evaluate-rules` and it writes into `risk_alert.detail`. "The
                   rule engine" is the thing the reader already knows about from this screen — the
                   Evaluated column prints whether it measures a rule and how often — and the
                   claim is unchanged: signals exist on an alert because the engine put them
                   there, which is why a seeded alert has none. Neither name belonged in a
                   `.tabular` span, which is for a literal the reader is looking at. */}
                <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
                  <Icon name="warning-circle" />
                  <div>
                    These are every signal the alert holds, and how many there are depends on WHO
                    RAISED THE ALERT. An alert from the rule engine carries its arithmetic — the
                    deposits counted and the window they fell in, or the payout ratio and the
                    totals behind it — because that engine writes them onto the alert as it
                    raises it. An alert loaded as sample data carries one sentence and nothing
                    else. The rest of a full evidence snapshot is not on the case yet, for
                    two different reasons. <strong>Device and IP</strong> are genuinely not held:
                    nothing on this platform reads anything from a player&rsquo;s device, which is
                    why the rule book calls the shared-signup rule explicitly NOT a device
                    fingerprint. <strong>Linked accounts</strong> are a different matter — they
                    ARE recorded, and two rules read them every five minutes; this panel does not
                    fetch them, so the gap is a read nobody added rather than a record that does
                    not exist.
                  </div>
                </div>
              </Panel>

              <Panel title="Case timeline">
                <div className="feed">
                  <div className="feed-row">
                    <span className="feed-time">{clock.format(new Date(selected.created_at))}</span>
                    <span>
                      Rule fired · {selected.rule} v{selected.rule_version}
                      {/*
                        THE CLAIM, AND WHETHER IT HAPPENED.
                        This line used to read "Critical: pending withdrawals
                        auto-hold until this closes" on every Critical alert, and
                        NOTHING PERFORMED THE HOLD — payment.withdrawal was moved
                        into HELD_BY_RISK by the seed and by nothing else. The
                        hold is real now (risk.reconcile_critical_holds, applied
                        by the worker and lifted by the close), so the sentence
                        reports the count this alert is actually holding rather
                        than asserting a policy.
                      */}
                      {/* PRESENT TENSE, because the number is a present-tense
                          count. `held_by_this_alert` counts withdrawals
                          CURRENTLY held under this alert's reference, and
                          closing the alert releases them in the same
                          transaction — so the count returns to zero and the old
                          past-tense wording told an operator "nothing was
                          pending to hold when this fired" about an alert that
                          had just held two withdrawals and released them. A
                          closed alert cannot report what it once held from a
                          live count, so it does not claim to. */}
                      {selected.severity === 'CRITICAL'
                        ? Number(selected.held_by_this_alert) > 0
                          ? ` · Critical: holding ${formatCount(Number(selected.held_by_this_alert))} pending withdrawal${
                              Number(selected.held_by_this_alert) === 1 ? '' : 's'
                            }, released when this alert closes`
                          : selected.closed_at
                            ? ' · Critical: holding nothing now — any withdrawals it held were released when it closed'
                            : ' · Critical: nothing pending to hold'
                        : ''}
                    </span>
                  </div>
                  {selected.closed_at ? (
                    <div className="feed-row">
                      <span className="feed-time">{clock.format(new Date(selected.closed_at))}</span>
                      <span>
                        Closed as {STATUS_LABEL[selected.status] ?? selected.status}
                        {selected.closed_by ? ` by ${selected.closed_by}` : ''}
                        {selected.closing_note ? ` · “${selected.closing_note}”` : ''}
                      </span>
                    </div>
                  ) : null}
                </div>

                {/*
                   The two columns are `created_at` and `closed_at`. The feed above already shows
                   both of them as times an operator can read, so naming them added nothing they
                   could use and dressed two schema details as values on their screen. "When it
                   was raised, and when it was closed" is the same pair of facts, and the point of
                   the sentence — that the missing steps have nowhere to be recorded — is
                   untouched. */}
                <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
                  <Icon name="clock-counter-clockwise" />
                  <div>
                    Two events, because two are all that are recorded: when it was raised, and when
                    it was closed. Nothing keeps the steps in between — threshold crossed, bet
                    pattern shifted, a previous alert cleared — so they have nowhere to be recorded
                    and are not invented here.
                  </div>
                </div>
              </Panel>

              <Panel title={`Case file · ${selected.player ?? 'unassigned'}`}>
                <div className="stack">
                  {playerCase ? (
                    <>
                      <dl className="stack" style={{ margin: 0 }}>
                        <div className="def-row">
                          <dt>Case</dt>
                          <dd className="tabular">{playerCase.ref}</dd>
                        </div>
                        <div className="def-row">
                          <dt>Status</dt>
                          <dd>
                            <Tag tone={playerCase.status === 'OPEN' ? 'neutral' : 'outline'}>
                              {CASE_STATUS_LABEL[playerCase.status] ?? playerCase.status}
                            </Tag>
                          </dd>
                        </div>
                        <div className="def-row">
                          <dt>Opened by</dt>
                          <dd>{playerCase.opened_by ?? '—'}</dd>
                        </div>
                        {playerCase.closed_by ? (
                          <div className="def-row">
                            <dt>Closed by</dt>
                            <dd>{playerCase.closed_by}</dd>
                          </div>
                        ) : null}
                        {playerCase.summary ? (
                          <div className="def-row">
                            <dt>Summary</dt>
                            <dd style={{ textAlign: 'left' }}>{playerCase.summary}</dd>
                          </div>
                        ) : null}
                      </dl>

                      {/* ── The findings ──────────────────────────────────────
                          The case's whole content. Fetched separately because
                          the list endpoint returns a COUNT and a case is worth
                          nothing to an investigator as a number. */}
                      <span className="panel-title">
                        Findings ({formatCount(caseFile?.findings.length ?? Number(playerCase.finding_count))})
                      </span>
                      {loadingCase ? (
                        <div className="text-muted" style={{ fontSize: '13px' }}>
                          Loading the file…
                        </div>
                      ) : caseFile && caseFile.findings.length > 0 ? (
                        <div className="feed">
                          {caseFile.findings.map((f) => (
                            <div className="feed-row" key={f.id}>
                              <span className="feed-time">{relative(f.created_at)}</span>
                              <span>
                                {f.kind === 'RESOLUTION' ? (
                                  <Tag tone="outline">Verdict</Tag>
                                ) : null}{' '}
                                {f.body}
                                <div className="text-subtle" style={{ fontSize: '11px' }}>
                                  {f.author_label}
                                </div>
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted" style={{ fontSize: '13px' }}>
                          Nothing has been recorded on this case yet.
                        </div>
                      )}

                      {playerCase.status === 'OPEN' ? (
                        <div className="row">
                          <Button onClick={() => openCaseAction('finding')}>Record finding</Button>
                          <Button variant="primary" onClick={() => openCaseAction('resolve')}>
                            Resolve case
                          </Button>
                        </div>
                      ) : (
                        <div className="text-muted" style={{ fontSize: '13px' }}>
                          This case is closed as{' '}
                          {(CASE_STATUS_LABEL[playerCase.status] ?? playerCase.status).toLowerCase()}
                          . A verdict is final and the file takes no further findings — anything new
                          is a new case.
                        </div>
                      )}

                      {/* Answered where the question is asked. The API returns
                          this sentence; the screen does not compose it. */}
                      {caseFile && !caseFile.attachments.supported ? (
                        <div className="text-subtle" style={{ fontSize: '11px' }}>
                          No attachments: {caseFile.attachments.reason}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <div className="text-muted" style={{ fontSize: '13px' }}>
                        No risk case has been opened for this player. Closing an alert as{' '}
                        <strong>Escalated</strong> opens one automatically; open one here if the
                        investigation started somewhere other than an alert.
                      </div>
                      <div className="row">
                        <Button
                          onClick={() => openCaseAction('open')}
                          disabled={!selected.player_id}
                          title={
                            selected.player_id
                              ? undefined
                              : 'This alert names no player, so there is nobody to open a case about.'
                          }
                        >
                          Open a case
                        </Button>
                      </div>
                    </>
                  )}

                  <span className="panel-title">Every alert for this player</span>
                  <div className="feed">
                    {playerAlerts.map((a) => (
                      <div className="feed-row" key={a.id}>
                        <span className="feed-time">{relative(a.created_at)}</span>
                        <span>
                          <span className="tabular">{a.ref}</span> · {a.rule} ·{' '}
                          <Tag tone={statusTone(a.status)}>
                            {STATUS_LABEL[a.status] ?? a.status}
                          </Tag>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/*
                   There is no `case_id` on `risk_alert` and no alert list on `risk_case`. Said as
                   "an alert is never attached to a case, and a case keeps no list of alerts" it
                   is the same missing link in both directions, and it explains what the reader
                   can see for themselves: the case above was found by player, not by any tie to
                   this alert. The names stay here, where whoever adds that link will look. */}
                <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
                  <Icon name="warning-circle" />
                  <div>
                    Repeat patterns are aggregated by player, not by case: an alert is never
                    attached to a case and a case keeps no list of alerts, so the case
                    above is matched on the player reference and this list is every alert loaded for
                    that player in the current brand scope — not necessarily every alert that exists.
                  </div>
                </div>
              </Panel>
            </>
          )}
        </div>
      </div>

      {/* ── Close verdict ─────────────────────────────────────────────────── */}
      {/*
        NOT DISMISSIBLE, AND THE GUARD BELONGS ON THIS SIDE OF THE BUTTON.

        Escape used to close this dialog outright, and what it threw away was
        never a request in flight — it was the decision itself. By the time this
        is open the operator has picked the alert out of the queue, chosen a
        verdict, chosen a control and a reason from two lists if the verdict is
        Restricted, and typed a closing note the route will not take under three
        characters, usually while reading the alert beside it. A stray Escape
        drops all of it, says nothing about having done so, and leaves the alert
        sitting open behind the dialog, so the only trace is a queue that did not
        change — which reads as a platform that ignored the press.

        `false` outright rather than `{!closing}`, which is how the rest of the
        console now has it: a guard that only holds while the write is in flight
        covers the one moment when the verdict has already been given, and leaves
        the moment before the press — the one that matters — wide open. Cancel is
        beside the confirm button, names itself, and is the only way out.
      */}
      <Dialog
        open={verdict !== null && selected !== null}
        onClose={() => setVerdict(null)}
        dismissible={false}
        title={
          verdict === 'RESTRICTED'
            ? `Restrict ${selected?.player ?? 'this player'}?`
            : verdict === 'CLEARED'
              ? `Clear ${selected?.ref ?? 'this alert'}?`
              : `Escalate ${selected?.ref ?? 'this alert'} to compliance?`
        }
        actions={
          <>
            <Button onClick={() => setVerdict(null)} disabled={closing}>
              Cancel
            </Button>
            <Button variant="primary" busy={closing} onClick={() => void submitClose()}>
              {verdict ? CLOSE_COPY[verdict].cta : 'Close alert'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {/*
            DANGER RATHER THAN WARN, AND THE SAME TONE FOR ALL THREE VERDICTS.

            The contract written beside these two classes is narrow: warn is a
            reversible consequence or a limit about to be met, danger is the
            irreversible or anything that moves money. Every button on this
            dialog is the second. The verdict cannot be given twice and no screen
            takes it back; Restricted applies a control to the account in the
            same breath as the verdict, and the control an operator reaches for
            most often is the one that refuses the player's payouts; and closing
            settles the platform's own hold on that player's held withdrawals
            whichever verdict was chosen. None of the three is the lighter tone,
            so none of them gets it — a warn here would only teach the operator
            that the colour on this screen means nothing.

            The SENTENCE changes instead of the colour. `body` is the design's
            own copy for the verdict, kept word for word from the grey paragraph
            that used to sit here under the title, and `beyond` is what a grey
            paragraph never managed to say: that there is one press, and where
            the way back is.

            The quiet notice at the foot of the dialog stays quiet deliberately.
            That one is the list of things the press does NOT do — nobody is
            mailed, no SAR is drafted — which is reassurance rather than
            consequence, and a second coloured block would leave the operator
            choosing which of two warnings to read.
          */}
          <div className="notice notice-danger">
            <Icon name="warning" />
            <div>
              {verdict ? `${CLOSE_COPY[verdict].body} ${CLOSE_COPY[verdict].beyond}` : null}
            </div>
          </div>

          {/* Which control, asked only for the verdict that applies one. The
              operator is making this decision, so they name it — a default
              would restrict a player in a way nobody chose. */}
          {verdict === 'RESTRICTED' ? (
            <>
              {/* NO TABLE NAME. This hint read "Written to
                  player.player_restriction and in force the moment this
                  closes." The destination is `player.player_restriction` — one
                  active row per (player, kind), inserted by
                  `POST /api/risk/alerts/:id/close` — and the identifier is
                  recorded here, for whoever reads the source, because a risk
                  officer choosing between four controls cannot look it up, ask
                  anybody for it, or decide differently for knowing it.

                  WHAT THE SENTENCE WAS ACTUALLY TELLING THEM IS WORTH KEEPING,
                  and it is two separate facts, which is why the rewrite is
                  longer rather than shorter.

                  First, the control is recorded against the PLAYER, not against
                  this alert. It therefore outlives the alert: it keeps applying
                  wherever that account is looked at, long after this case is
                  closed, and lifting it is a separate deliberate act on the
                  player's own screen. An operator who read it as "a flag on
                  this alert" would under-rate what they were doing.

                  Second, there is no gap between the verdict and the effect.
                  The close route inserts the restriction inside the SAME
                  transaction as the verdict — `effective_from now()`, `active`
                  true — so either both land or neither does, and nothing queues
                  it for a second pair of eyes first. The person choosing from
                  this list is the person applying it.

                  (If the player already has an active control of the same kind
                  the route keeps the existing one rather than stacking a second,
                  and says so in its reply. Either way the sentence below holds:
                  the control is in force once this closes.) */}
              <Field
                label="Control to apply"
                hint="Recorded against the player's account, not just this alert, and in force the moment this closes."
              >
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={restrictionKind}
                    onChange={(e) => setRestrictionKind(e.target.value as StaffRestrictionKind)}
                  >
                    <option value="WITHDRAWAL_BLOCK">Block withdrawals</option>
                    <option value="LIVE_RESTRICTED">Restrict live games</option>
                    <option value="BONUS_EXCLUSION">Exclude from bonuses</option>
                    <option value="DEPOSIT_LIMIT">Limit deposits</option>
                  </Select>
                )}
              </Field>
              <Field
                label="Reason"
                hint="A closed set, so a compliance report can count restrictions by cause. Your note rides with the audit row."
              >
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={restrictionReason}
                    onChange={(e) => setRestrictionReason(e.target.value as RestrictionReasonCode)}
                  >
                    <option value="RISK_REVIEW">Risk review</option>
                    <option value="FRAUD_INVESTIGATION">Fraud investigation</option>
                    <option value="CHARGEBACK">Chargeback</option>
                    <option value="KYC_INCOMPLETE">KYC incomplete</option>
                    <option value="REGULATORY_ORDER">Regulatory order</option>
                  </Select>
                )}
              </Field>
            </>
          ) : null}

          <Field
            label="Closing note"
            error={noteError}
            hint="Saved to the alert with your name and the time, and copied into the audit log."
          >
            {({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                value={note}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => {
                  setNote(e.target.value);
                  setNoteError(undefined);
                }}
                placeholder="What you checked, and what you concluded"
              />
            )}
          </Field>

          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>{verdict ? CLOSE_COPY[verdict].gap : null}</div>
          </div>
        </div>
      </Dialog>

      {/* ── The case file's three writes ──────────────────────────────────────
          One dialog, three actions, for the reason the rule editor is one
          dialog with a mode: a half-typed finding and a half-typed verdict must
          never be postable to each other's endpoint. */}
      <Dialog
        open={caseAction !== null}
        onClose={() => setCaseAction(null)}
        title={
          caseAction === 'resolve'
            ? `Resolve ${playerCase?.ref ?? 'this case'}?`
            : caseAction === 'open'
              ? `Open a case on ${selected?.player ?? 'this player'}?`
              : `Record a finding on ${playerCase?.ref ?? 'this case'}`
        }
        actions={
          <>
            <Button onClick={() => setCaseAction(null)} disabled={savingCase}>
              Cancel
            </Button>
            <Button variant="primary" busy={savingCase} onClick={() => void submitCaseAction()}>
              {caseAction === 'resolve'
                ? 'Record verdict'
                : caseAction === 'open'
                  ? 'Open case'
                  : 'Save finding'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {caseAction === 'resolve' ? (
            <Field
              label="Verdict"
              hint="A closed set, so a compliance review can count outcomes. Inconclusive is offered on purpose — recording an unsettled case as cleared is what makes the cleared count unreadable."
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={caseVerdict}
                  onChange={(e) => setCaseVerdict(e.target.value as CaseVerdict)}
                >
                  {CASE_VERDICTS.map((v) => (
                    <option key={v.key} value={v.key}>
                      {v.label} — {v.hint}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          <Field
            label={
              caseAction === 'resolve'
                ? 'What the verdict rests on'
                : caseAction === 'open'
                  ? 'What the case is about'
                  : 'What you found'
            }
            error={caseTextError}
            hint="Written to the case with your name and the time. Findings are append-only: this cannot be edited or removed afterwards."
          >
            {({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                className="input"
                rows={4}
                value={caseText}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => {
                  setCaseText(e.target.value);
                  setCaseTextError(undefined);
                }}
                placeholder={
                  caseAction === 'resolve'
                    ? 'What you checked, and why it settles the case'
                    : 'What you checked, who you spoke to, and what it showed'
                }
              />
            )}
          </Field>

          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>
              {caseAction === 'resolve'
                ? 'A verdict is final: the case takes no further findings and cannot be resolved twice. Nothing about the player changes — resolving a case does not lift a restriction, release a withdrawal or notify anybody.'
                : 'Written findings only. Nothing on this screen has been wired to the file store the platform already uses for support attachments, so a screenshot, a bank letter or an ID scan cannot be attached here — describe them and keep them wherever your team already does.'}
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── Rule thresholds ───────────────────────────────────────────────── */}
      <Dialog
        open={dialog === 'thresholds'}
        onClose={() => {
          closeRuleEditor();
          setDialog(null);
        }}
        title="Rule thresholds"
        wide
        actions={
          <Button
            onClick={() => {
              closeRuleEditor();
              setDialog(null);
            }}
          >
            Close
          </Button>
        }
      >
        {/*
         * The status written is `HELD_BY_RISK`. "Held by risk" is what the withdrawals queue
         * prints for it and the chip it filters by, so the sentence now points at the place the
         * hold is visible instead of at the constant behind it — which matters here, because the
         * rest of the sentence is about what an operator will and will not see happen to those
         * rows.
         */}
        <p className="text-muted" style={{ margin: 0, fontSize: '13px' }}>
          A threshold change applies to new events only: closed alerts keep the verdict, the note
          and the closer they were given, and nothing re-runs a rule over history. A rule that
          raises a <strong>Critical</strong> alert moves that player&rsquo;s pending withdrawals
          to <strong>Held by risk</strong> until the alert is closed &mdash; which
          moves them in the queue and attaches a reason; it does not refuse a release. Read the
          Evaluated column before tuning: a rule that nothing measures will accept a threshold
          and do nothing with it. The panel above names any such rule, by code, from the data —
          this sentence used to name a count instead, and said two when the answer was none.
        </p>

        <div className="stack" style={{ marginTop: 'var(--space-3)' }}>
          <DataTable
            caption="Risk rules with their stored threshold, severity, version and 30-day fire count"
            columns={ruleBookColumns}
            rows={rules}
            rowKey={(r) => r.id}
            empty={
              <EmptyState icon="list-checks" title="No rules configured">
                Nothing is being evaluated, so there is no threshold to review.
              </EmptyState>
            }
          />

          {editingRule ? (
            <Panel
              title={
                ruleEdit?.mode === 'threshold'
                  ? `Threshold · ${editingRule.name}`
                  : `${editingRule.enabled ? 'Disable' : 'Enable'} · ${editingRule.name}`
              }
            >
              <div className="stack">
                {ruleEdit?.mode === 'threshold' ? (
                  (editingShape ?? []).map((field) => (
                    <Field
                      key={field.key}
                      label={field.label}
                      hint={field.hint}
                      error={ruleFieldErrors[field.key]}
                    >
                      {({ id, describedBy, invalid }) => (
                        <Input
                          id={id}
                          inputMode="numeric"
                          value={thresholdDraft[field.key] ?? ''}
                          aria-describedby={describedBy}
                          aria-invalid={invalid || undefined}
                          onChange={(e) => {
                            const next = e.target.value;
                            setThresholdDraft((draft) => ({ ...draft, [field.key]: next }));
                            setRuleFieldErrors((prev) => {
                              if (!prev[field.key]) return prev;
                              const rest = { ...prev };
                              delete rest[field.key];
                              return rest;
                            });
                          }}
                        />
                      )}
                    </Field>
                  ))
                ) : (
                  <p className="text-muted" style={{ margin: 0, fontSize: '13px' }}>
                    {editingRule.enabled
                      ? `Disabling stops ${editingRule.name} raising NEW alerts. The alerts it has already raised stay open and still need a verdict — switching a rule off is not a way to clear its queue.`
                      : `Enabling lets ${editingRule.name} raise alerts again, from now on. It does not re-evaluate anything that happened while it was off.`}
                  </p>
                )}

                <Field
                  label="Reason"
                  hint="A closed set, so a control review can count why thresholds moved and why rules were switched off."
                >
                  {({ id }) => (
                    <Select
                      id={id}
                      value={ruleReason}
                      onChange={(e) => setRuleReason(e.target.value)}
                    >
                      {RULE_REASONS.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field
                  label="Note"
                  error={ruleFieldErrors['note']}
                  hint="Written to the hash-chained audit log with your name, the old value and the new one."
                >
                  {({ id, describedBy, invalid }) => (
                    <textarea
                      id={id}
                      className="input"
                      rows={3}
                      value={ruleNote}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      onChange={(e) => {
                        setRuleNote(e.target.value);
                        setRuleFieldErrors((prev) => {
                          if (!prev['note']) return prev;
                          const rest = { ...prev };
                          delete rest['note'];
                          return rest;
                        });
                      }}
                      placeholder="What you observed, and what the new setting is meant to catch"
                    />
                  )}
                </Field>

                <div className="row">
                  <Button onClick={closeRuleEditor} disabled={savingRule}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    className="header-spacer"
                    busy={savingRule}
                    onClick={() =>
                      void (ruleEdit?.mode === 'threshold' ? saveRuleThreshold() : saveRuleState())
                    }
                  >
                    <Icon name="check-circle" />
                    {ruleEdit?.mode === 'threshold'
                      ? 'Save threshold'
                      : editingRule.enabled
                        ? 'Disable rule'
                        : 'Enable rule'}
                  </Button>
                </div>
              </div>
            </Panel>
          ) : null}

          {/*
           * Three names left this paragraph. A threshold lives in `risk_rule.config`, the
           * enable/disable write is the rule's `/state` endpoint, and an alert carries only a
           * `rule_id`. None of the three is something an operator can look at or ask for: what
           * they have is the Threshold and Version columns in the table above and the two
           * editors this dialog opens, so the sentences describe those and say the same things
           * about them — the threshold is kept on the rule, both writes need the same capability,
           * and the version is left alone because an alert points at the rule rather than at a
           * version of it. The permission keys stay as they were; this screen is where an
           * operator is told which capability they are short of.
           */}
          <div className="notice notice-quiet" id="threshold-gap">
            <Icon name="info" />
            <div>
              A threshold is stored on the rule itself and is edited
              here: changing a threshold and switching a rule on or off are both gated on{' '}
              <span className="tabular">players.restrict</span> — the same capability that opens
              this screen and closes an alert, because deciding what raises an alert is the same
              job as acting on one. It was <span className="tabular">access.manage</span>, which is
              &ldquo;manage roles &amp; users&rdquo; and only the Super Admin holds it, so the
              officer this rule book belongs to could read it and change nothing.{' '}
              {/* In order: VELOCITY_DEPOSIT, PAYOUT_RATIO, SHARED_INSTRUMENT, BONUS_ABUSE. */}
              The shape is checked against the
              rule it belongs to: the deposit-velocity rule takes a number of deposits and a window,
              the payout-ratio rule takes a ratio in basis points, the shared-payment-instrument and
              bonus-abuse rules each take a number of accounts and a window in days, and anything
              else is refused rather than stored. Those last two had no settings and no editor for
              as long as nothing measured them;{' '}
              linked-account records gave both something to measure, so
              both now carry a real threshold — the engine skips a rule whose threshold it cannot
              read, and a control refused every five minutes is worse than one that is switched
              off. <strong>Edit threshold</strong> is unavailable only on a rule this console has
              no shape for, which is one somebody added outside this console: there is nothing to
              validate against and nothing is invented, and it can still be enabled and disabled.
              The <em>Evaluated</em> column shows the other half of the same honesty — a rule can
              be switched on with nothing to read, so it prints the coverage it counted rather than
              leaving the silence to be misread.{' '}
              The <strong>Version</strong> in the table above is deliberately not moved by an
              edit: an alert points at the rule and not at a version of it, so bumping the version
              on the row every past alert points at would relabel each of them as having fired at a
              version that did not yet exist. The before and after values are in the audit log,
              which is the configuration log. Neither write demands a second factor, because{' '}
              <span className="tabular">players.restrict</span> does not: the same session may
              already bar an account without one, and both changes are audited with a reason code
              and a note.
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── Risk report export ────────────────────────────────────────────── */}
      <Dialog
        open={dialog === 'export'}
        onClose={() => setDialog(null)}
        title="Export risk alerts"
        wide
        actions={
          <>
            <Button onClick={() => setDialog(null)}>Close</Button>
            {canExportAlerts ? (
              <Button
                variant="primary"
                busy={exporting}
                disabled={exporting}
                onClick={() => void runAlertExport()}
              >
                {exported ? 'Run it again' : 'Create the file'}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="download-simple" />
            <div>
              The file is <strong>CSV</strong>, built when you press the button and downloaded
              here. One row per alert, carrying when it was raised, the rule, the severity, the
              player&rsquo;s reference, and — for a closed alert — when it was closed and what the
              person who closed it wrote.
            </div>
          </div>

          <div className="halves">
            <Field label="First day" {...(exportDateError ? { error: exportDateError } : {})}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="date"
                  value={exportFrom}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  onChange={(e) => setExportFrom(e.target.value)}
                />
              )}
            </Field>
            <Field label="Last day" hint="Inclusive, on the Asia/Manila gaming day.">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  type="date"
                  value={exportTo}
                  aria-describedby={describedBy}
                  onChange={(e) => setExportTo(e.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Which alerts">
            {({ id }) => (
              <Select id={id} value={exportScope} onChange={(e) => setExportScope(e.target.value)}>
                {EXPORT_SCOPES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {/* The two things the old form promised that no schema can answer.
              Stated here rather than shipped as empty columns. */}
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              <strong>Two things are not in this file, and cannot be.</strong>{' '}
              <strong>Rule fires</strong> are not counted anywhere — a rule that evaluated a
              thousand times and raised two alerts looks identical to one that evaluated twice — so
              the file carries alerts raised, never attempts made. And{' '}
              <strong>confirmed fraud</strong> is not recorded: closing an alert carries a written
              note, not a verdict from a fixed list, so nothing can add up a fraud total. The
              closing notes are in the file; a count of confirmations is not something this platform
              knows.
            </div>
          </div>

          {exported ? (
            <div className="notice">
              <Icon name={exported.truncated ? 'warning-circle' : 'check-circle'} />
              <div>
                <strong>{exported.ref}</strong> · {formatCount(exported.rowCount)}{' '}
                {exported.rowCount === 1 ? 'alert' : 'alerts'}.
                {exported.truncated ? (
                  <>
                    {' '}
                    <strong>This file is short.</strong> The row cap stopped it before the end of
                    the answer — narrow the dates and run it again before you send it anywhere.
                  </>
                ) : null}
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <a className="btn btn-secondary" href={exported.downloadPath} download>
                    Download {exported.ref}.csv
                  </a>
                </div>
              </div>
            </div>
          ) : null}

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/* The reporting role is a read-only database role with no grant on the identity
                  schema's tables. */}
              <strong>The file names a closer by admin ID, not by name.</strong> A report runs with
              read-only access that is deliberately kept away from account and sign-in records — the same
              restriction behind the promise that reporting cannot reach credentials — so a
              display name is not something an export can fetch. Resolve the id in{' '}
              <strong>Access control</strong> if you need it.
              {canExportAlerts ? null : (
                <>
                  {' '}
                  Your role cannot run this export: it needs{' '}
                  <span className="signin-perm">players.restrict</span> to read alerts AND{' '}
                  <span className="signin-perm">audit.export</span> to take a copy off the platform.
                </>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}

/**
 * WHAT THE THREE FIGURES ABOVE THE QUEUE COUNT, each as its tile counts it.
 *
 * It said confirmed fraud "is not modelled anywhere in the schema … so it is reported as untracked",
 * directly under the Confirmed fraud tile — whose own comment calls that wording false (staging,
 * 15 Oct, S13). Every claim below is the query's: the held withdrawals and their players are counted
 * by `GET /api/risk/alerts` over the list's own filter, with no amount in the answer; rules fired is
 * the sum of `fired_30d`, alerts created in 30 days and read for every brand; confirmed fraud is
 * `risk_case` rows closed CONFIRMED in 30 days inside the reader's brands, with cleared and open
 * beside it (backoffice/operations.ts).
 */
export function RiskFiguresNotice() {
  return (
    <div className="notice notice-quiet">
      <Icon name="warning-circle" />
      <div>
        <strong>What these figures are, exactly.</strong> &ldquo;Withdrawals held&rdquo; counts the
        withdrawals waiting under <strong>Awaiting checker</strong> or <strong>Held by risk</strong>{' '}
        on the Withdrawals queue for the players with an alert in this list, and the line under it
        counts those players once each. It is a count: no amount comes with it, so none is shown.
        &ldquo;Rules fired&rdquo; counts the alerts the rules raised in the last 30 days, across
        every brand. &ldquo;Confirmed fraud&rdquo; counts the cases closed as{' '}
        <strong>Confirmed</strong> in the last 30 days, in the brands you can see; the line under it
        gives the cases cleared in those 30 days and the cases still open.
      </div>
    </div>
  );
}
